import type { SiteNode } from "@energyplatform/client";

/** Path from the root to the node, for breadcrumbs. */
export function pathTo(
  nodes: readonly SiteNode[],
  id: string | undefined,
): SiteNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const path: SiteNode[] = [];
  let current = id === undefined ? undefined : byId.get(id);
  while (current && !path.includes(current)) {
    path.unshift(current);
    current =
      current.parentId === null ? undefined : byId.get(current.parentId);
  }
  return path;
}

/** Nodes in depth-first order, children by name, so a flat list reads like the tree. */
export function depthFirst(
  nodes: readonly SiteNode[],
): { node: SiteNode; depth: number }[] {
  const out: { node: SiteNode; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const n of nodes
      .filter((x) => x.parentId === parent)
      .sort((a, b) => a.name.localeCompare(b.name))) {
      out.push({ node: n, depth });
      walk(n.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** Ids of the node and everything below it. */
export function subtreeOf(nodes: readonly SiteNode[], id: string): string[] {
  const out = [id];
  for (let i = 0; i < out.length; i++) {
    for (const n of nodes) {
      if (n.parentId === out[i] && !out.includes(n.id)) {
        out.push(n.id);
      }
    }
  }
  return out;
}

export interface TreeRow {
  node: SiteNode;
  depth: number;
  /** For each level above and including this one: whether that ancestor (or the node) is the last of its siblings. */
  lasts: boolean[];
}

/**
 * Rows of a tree with what tree guides need (prototype "guides"): depth-first, children by name. With a query only
 * nodes whose name matches, or that have a matching descendant, are kept, so a match is shown with its path.
 */
export function treeRows(nodes: readonly SiteNode[], query = ""): TreeRow[] {
  const q = query.trim().toLocaleLowerCase();
  const kids = (parent: string | null) =>
    nodes
      .filter((x) => x.parentId === parent)
      .sort((a, b) => a.name.localeCompare(b.name));
  const hit = (n: SiteNode): boolean =>
    q === "" || n.name.toLocaleLowerCase().includes(q) || kids(n.id).some(hit);
  const out: TreeRow[] = [];
  const walk = (parent: string | null, depth: number, lasts: boolean[]) => {
    const shown = kids(parent).filter(hit);
    shown.forEach((n, i) => {
      const own = depth === 0 ? [] : [...lasts, i === shown.length - 1];
      out.push({ node: n, depth, lasts: own });
      walk(n.id, depth + 1, own);
    });
  };
  walk(null, 0, []);
  return out;
}
